import React, { useMemo, useState } from "react";
import { Button } from "../../../../shadcn/components/ui/button";
import CustomModalLayout from "../../../web/Auth/CustomModalLayout";
import SimpleBidHistoryCard from "./SimpleBidHistoryCard";

interface BidHistoryItem {
  id: string;
  user_uuid: string;
  bidder_name: string;
  bidding_amount: number;
  bid_timestamp: number;
  status: "Success" | "Failed";
  meta?: {
    initial_bid?: boolean;
  };
}

interface SimpleSwissBidHistoryProps {
  bids: BidHistoryItem[];
  currentUserUuid: string;
  isSwissAuction: boolean;
}

const previewBidCount = 3;

const filterSwissInitialBids = (bids: BidHistoryItem[]) => {
  const initialBids = bids.filter((bid) => bid?.meta?.initial_bid === true);
  const nonInitialBids = bids.filter((bid) => bid?.meta?.initial_bid !== true);
  if (initialBids.length === 0) {
    return bids;
  }
  const lastInitialBid = initialBids[initialBids.length - 1];
  return [...nonInitialBids, lastInitialBid];
};

export default function SimpleSwissBidHistory({
  bids,
  currentUserUuid,
  isSwissAuction,
}: SimpleSwissBidHistoryProps) {
  const [openModal, setOpenModal] = useState(false);

  const processedBids = useMemo(() => {
    const sourceBids = isSwissAuction ? filterSwissInitialBids(bids) : bids;
    return [...sourceBids].sort(
      (firstBid, secondBid) => secondBid.bid_timestamp - firstBid.bid_timestamp,
    );
  }, [bids, isSwissAuction]);

  const previewBids = processedBids.slice(0, previewBidCount);

  return (
    <>
      <section className="bg-[#FAF7FA] px-2 lg:px-4 pt-2">
        <div className="flex justify-between text-sm mb-2 border-b">
          <div>Bid History</div>
          {processedBids.length > 0 && (
            <Button
              className="h-8 text-sm bg-[#FAF7FA] underline text-brand-textBlack hover:bg-[#FAF7FA]"
              onClick={() => setOpenModal(true)}
            >
              View All
            </Button>
          )}
        </div>
        <div className="flex flex-col gap-2 max-h-[240px] overflow-y-auto scrollbar-hide pb-3">
          {previewBids.length > 0 ? (
            previewBids.map((bid, index) => (
              <SimpleBidHistoryCard
                key={bid.id}
                bid={bid}
                index={index}
                currentUserUuid={currentUserUuid}
                isSwissAuction={isSwissAuction}
              />
            ))
          ) : (
            <div className="text-xs text-brand-textGrey pb-3">
              No bid history available for this event.
            </div>
          )}
        </div>
      </section>

      <CustomModalLayout
        open={openModal}
        onClose={() => setOpenModal(false)}
        childrenClassName="pb-0 px-4"
        className="pt-4 lg:pb-[30px] w-full mx-4 max-w-[650px]"
      >
        <div className="text-sm font-openSans text-brand-textBlack font-semibold">
          Bid History
        </div>
        <div className="mt-4 max-h-[400px] overflow-y-auto flex flex-col gap-2">
          {processedBids.length > 0 ? (
            processedBids.map((bid, index) => (
              <SimpleBidHistoryCard
                key={bid.id}
                bid={bid}
                index={index}
                currentUserUuid={currentUserUuid}
                isSwissAuction={isSwissAuction}
              />
            ))
          ) : (
            <div className="text-xs text-brand-textGrey pb-3">
              No bid history available for this event.
            </div>
          )}
        </div>
      </CustomModalLayout>
    </>
  );
}
